import React from 'react';
import { motion } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

const sleepStages = [
  { stage: 'Awake', duration: 18, color: '#f59e0b', pct: 10 },
  { stage: 'REM', duration: 94, color: '#8b5cf6', pct: 22 },
  { stage: 'Light', duration: 180, color: '#3b82f6', pct: 42 },
  { stage: 'Deep', duration: 112, color: '#1d4ed8', pct: 26 },
];

const sleepTrendData = [
  { day: 'Mon', hours: 6.5, quality: 72 },
  { day: 'Tue', hours: 7.2, quality: 81 },
  { day: 'Wed', hours: 5.8, quality: 65 },
  { day: 'Thu', hours: 7.8, quality: 88 },
  { day: 'Fri', hours: 6.9, quality: 76 },
  { day: 'Sat', hours: 8.1, quality: 91 },
  { day: 'Sun', hours: 7.4, quality: 84 },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div className="bg-card border border-border/60 rounded-xl p-2.5 shadow-lg text-xs">
        <p className="font-semibold text-foreground mb-1">{label}</p>
        <p className="text-purple-500">{payload?.[0]?.value}h sleep</p>
        <p className="text-blue-500">{payload?.[1]?.value}% quality</p>
      </div>
    );
  }
  return null;
};

const SleepAnalysisPanel = ({ device = 'Whoop' }) => {
  const totalSleepMin = sleepStages?.reduce((a, s) => a + s?.duration, 0);
  const totalHours = (totalSleepMin / 60)?.toFixed(1);
  const qualityScore = 84;

  const getScoreColor = (score) => {
    if (score >= 85) return '#22c55e';
    if (score >= 70) return '#f59e0b';
    return '#ef4444';
  };

  const scoreColor = getScoreColor(qualityScore);

  return (
    <div className="bg-card rounded-2xl p-5 border border-border/40 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-purple-500/10 flex items-center justify-center">
            <Icon name="Moon" size={18} color="#8b5cf6" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-sm">Sleep Analysis</h3>
            <p className="text-xs text-muted-foreground">via {device}</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xl font-bold" style={{ color: scoreColor }}>{qualityScore}</div>
          <div className="text-xs text-muted-foreground">Quality Score</div>
        </div>
      </div>
      <div className="flex items-center gap-4 mb-4">
        <div className="flex-1">
          <div className="flex items-baseline gap-1 mb-1">
            <span className="text-2xl font-bold text-foreground">{totalHours}</span>
            <span className="text-sm text-muted-foreground">hrs</span>
          </div>
          <div className="text-xs text-muted-foreground">Last night · 10:42 PM – 6:18 AM</div>
        </div>
        <div className="flex gap-1.5">
          {[
            { label: 'Recovery', value: '78%', color: '#22c55e' },
            { label: 'Strain', value: '14.2', color: '#f97316' },
          ]?.map(s => (
            <div key={s?.label} className="bg-muted/30 rounded-xl px-3 py-2 text-center">
              <div className="text-sm font-bold" style={{ color: s?.color }}>{s?.value}</div>
              <div className="text-xs text-muted-foreground">{s?.label}</div>
            </div>
          ))}
        </div>
      </div>
      {/* Sleep Stage Timeline */}
      <div className="mb-4">
        <div className="flex h-5 rounded-lg overflow-hidden gap-0.5 mb-2">
          {sleepStages?.map((s) => (
            <motion.div
              key={s?.stage}
              className="rounded-sm"
              style={{ backgroundColor: s?.color, width: `${s?.pct}%` }}
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            />
          ))}
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          {sleepStages?.map((s) => (
            <div key={s?.stage} className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: s?.color }} />
              <span className="text-xs text-muted-foreground">{s?.stage}</span>
              <span className="text-xs font-medium text-foreground">{Math.floor(s?.duration / 60)}h {s?.duration % 60}m</span>
            </div>
          ))}
        </div>
      </div>
      {/* 7-day trend */}
      <div className="flex-1 min-h-0">
        <p className="text-xs text-muted-foreground mb-2 font-medium">7-Day Sleep Trend</p>
        <ResponsiveContainer width="100%" height={80}>
          <AreaChart data={sleepTrendData} margin={{ top: 2, right: 2, left: -30, bottom: 0 }}>
            <defs>
              <linearGradient id="sleepGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
            <YAxis hide domain={[4, 10]} />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="hours" stroke="#8b5cf6" strokeWidth={2} fill="url(#sleepGrad)" dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default SleepAnalysisPanel;
